import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { useSearchParams } from 'react-router-dom'
import { Navigation, MessageCircle, MapPin, Search, Crosshair, Check, X, Link2, Plus, Smartphone, RefreshCw, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react'
import api from '../api'
import { isAdmin } from '../auth'
import { Card, PageHeader, Button, Input, Badge, Tabs, Alert, EmptyState } from '../components/ui'
import { CLUSTER_MAX_ZOOM, CELL_PX, gridCluster, warnaKluster } from '../petaCluster'
import { LABEL_TIPE, WARNA_NODE, WARNA_ODP_PENUH, nodeIcon, KartuEditNode, KartuNode, TautanOdpModal } from '../components/PetaJaringanPanels'

// Peta Pelanggan (2026-09-20) -- fase 1: pin semua pelanggan berkoordinat, diwarnai status langganan.
// Fase 2: jaringan fisik OLT -> ODC -> ODP -> pelanggan (titik, garis penghubung, tautan pelanggan
// ke ODP, panel layer, riwayat perubahan). Leaflet dipakai langsung (tanpa react-leaflet, tidak ada
// di dependensi -- sama pola LokasiMap.jsx). Pin pelanggan = L.circleMarker (bukan L.marker) supaya
// tidak kena masalah ikon default Leaflet yg rusak di bundler CRA, & lebih ringan utk ratusan titik.
// Titik jaringan pakai L.marker + divIcon (lihat components/PetaJaringanPanels.jsx), sedangkan pin
// yg SEDANG diatur lokasinya (pelanggan maupun jaringan) = L.marker draggable.
const WARNA_STATUS = { AKTIF: '#16a34a', ISOLIR: '#d97706', BERHENTI: '#dc2626' }
const LABEL_STATUS = { AKTIF: 'Aktif', ISOLIR: 'Isolir', BERHENTI: 'Berhenti' }
const TONE_STATUS = { AKTIF: 'success', ISOLIR: 'warning', BERHENTI: 'danger' }
const warnaStatus = s => WARNA_STATUS[s] || '#6b6b78'
const LS_SEMBUNYI = 'peta_pelanggan_sembunyi'
const LS_LAYER = 'peta_pelanggan_layer'
const LS_MODE = 'peta_pelanggan_mode_warna'
const WARNA_BELUM_LOKASI = '#9ca3af'
const LABEL_TAGIHAN = { LUNAS: 'Lunas', BELUM: 'Belum bayar', TERLAMBAT: 'Terlambat' }
// Di bawah zoom ini overlay Bangunan tidak dimuat (area terlalu luas utk Overpass API, & outline
// bangunan cuma berguna saat sudah sedekat level rumah/RT) -- lihat routes/petaBangunan.js MAKS_TILE.
const AMBANG_ZOOM_BANGUNAN = 17

// Warna = SATU-SATUNYA penanda kondisi pelanggan (menggantikan sub-menu Belum ada / Sudah / Cek):
// pin di peta, titik di daftar, dan legenda memakai kategori yg sama, & legenda sekaligus filter.
//   Abu-abu (lingkaran kosong) = belum ada lokasi -> tidak punya pin di peta, cuma tampil di daftar.
//   Mode 'koneksi' (default): hijau online, merah offline (aktif tapi tak terhubung), kuning isolir,
//     gelap berhenti. Hijau muda = aktif tapi status koneksi belum diketahui (Mikrotik gagal/belum termuat).
//   Mode 'tagihan': hijau lunas, kuning belum bayar, merah terlambat, abu terang tanpa tagihan.
//   Lokasi mencurigakan (jauh dari pelanggan lain) BUKAN kategori warna: pin diberi garis hitam &
//   baris daftarnya ditandai peringatan, warnanya tetap mengikuti kondisi.
const MODE_WARNA = [['koneksi', 'Koneksi'], ['tagihan', 'Tagihan']]
const KAT_BELUM_LOKASI = { kunci: 'BELUM_LOKASI', label: 'Belum ada lokasi', warna: WARNA_BELUM_LOKASI }
const KATEGORI = {
  koneksi: [
    { kunci: 'ONLINE', label: 'Online', warna: '#16a34a' },
    { kunci: 'AKTIF_TAK_TAHU', label: 'Aktif (koneksi belum diketahui)', warna: '#86efac' },
    { kunci: 'OFFLINE', label: 'Offline', warna: '#dc2626' },
    { kunci: 'ISOLIR', label: 'Isolir', warna: '#d97706' },
    { kunci: 'BERHENTI', label: 'Berhenti', warna: '#334155' },
    KAT_BELUM_LOKASI
  ],
  tagihan: [
    { kunci: 'LUNAS', label: 'Lunas', warna: '#16a34a' },
    { kunci: 'BELUM', label: 'Belum bayar', warna: '#d97706' },
    { kunci: 'TERLAMBAT', label: 'Terlambat', warna: '#dc2626' },
    { kunci: 'TANPA', label: 'Tanpa tagihan', warna: '#cbd5e1' },
    KAT_BELUM_LOKASI
  ]
}

// Kategori satu pelanggan utk mode tertentu. `online` = Set username online, atau null kalau data
// Mikrotik belum termuat (pelanggan aktif jadi "belum diketahui", BUKAN dianggap offline).
function kategoriPin(i, mode, online) {
  if (i.lat === null) return 'BELUM_LOKASI'
  if (mode === 'tagihan') return i.tagihan || 'TANPA'
  if (i.status === 'ISOLIR') return 'ISOLIR'
  if (i.status !== 'AKTIF') return 'BERHENTI'
  if (online === null) return 'AKTIF_TAK_TAHU'
  return online.has(i.ppp_username) ? 'ONLINE' : 'OFFLINE'
}
function warnaPin(i, mode, online) {
  const k = kategoriPin(i, mode, online)
  const kat = KATEGORI[mode].find(x => x.kunci === k)
  return kat ? kat.warna : WARNA_BELUM_LOKASI
}
// Titik kecil (daftar & legenda): lingkaran kosong utk "belum ada lokasi", selain itu bulat penuh.
function gayaTitik(kat, ukuran) {
  const kosong = kat.kunci === 'BELUM_LOKASI'
  return {
    width: ukuran, height: ukuran, borderRadius: '50%', flexShrink: 0,
    background: kosong ? 'transparent' : kat.warna,
    border: kosong ? `2px solid ${kat.warna}` : '2px solid #fff',
    boxShadow: kosong ? 'none' : '0 0 0 1px rgba(0,0,0,.25)'
  }
}

function bacaModeTersimpan() {
  try {
    const v = localStorage.getItem(LS_MODE)
    if (MODE_WARNA.some(m => m[0] === v)) return v
  } catch {}
  return 'koneksi' // nilai lama ('status'/'online') dari versi sebelumnya otomatis jatuh ke sini
}

// Kategori yg disembunyikan lewat legenda, per mode. Diingat di browser -- try/catch krn
// localStorage bisa melempar (mode privat/diblokir).
function bacaSembunyiTersimpan() {
  const hasil = { koneksi: [], tagihan: [] }
  try {
    const v = JSON.parse(localStorage.getItem(LS_SEMBUNYI))
    if (v && typeof v === 'object') {
      for (const m of Object.keys(hasil)) {
        if (Array.isArray(v[m])) hasil[m] = v[m].filter(k => KATEGORI[m].some(x => x.kunci === k))
      }
    }
  } catch {}
  return hasil
}

function clusterIcon(n, warna) {
  const d = n < 10 ? 32 : n < 100 ? 38 : 44
  return L.divIcon({
    className: '', iconSize: [d, d], iconAnchor: [d / 2, d / 2],
    html: `<div style="width:${d}px;height:${d}px;border-radius:50%;background:${warna};border:3px solid rgba(255,255,255,.92);box-shadow:0 1px 5px rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center;color:#fff;font:700 13px system-ui,sans-serif">${n}</div>`
  })
}
const LAYER_DEFAULT = { pelanggan: true, odp: true, odc: true, olt: true, garis: true }

// Ingat pilihan layer di browser -- try/catch krn localStorage bisa melempar (mode privat/diblokir).
function bacaLayerTersimpan() {
  try {
    const v = JSON.parse(localStorage.getItem(LS_LAYER))
    if (v && typeof v === 'object') return Object.fromEntries(Object.keys(LAYER_DEFAULT).map(k => [k, typeof v[k] === 'boolean' ? v[k] : LAYER_DEFAULT[k]]))
  } catch {}
  return LAYER_DEFAULT
}

function pinIcon(warna) {
  return L.divIcon({
    className: '',
    iconSize: [34, 44],
    iconAnchor: [17, 42],
    html: `<svg width="34" height="44" viewBox="0 0 24 30" style="filter:drop-shadow(0 2px 2px rgba(0,0,0,.4))">
      <path d="M12 29C12 29 22 18.5 22 11.5a10 10 0 1 0-20 0C2 18.5 12 29 12 29z" fill="${warna}" stroke="#fff" stroke-width="1.6"/>
      <circle cx="12" cy="11.5" r="3.6" fill="#fff"/></svg>`
  })
}

const formatWaktu = w => (w ? `${w.slice(8, 10)}/${w.slice(5, 7)} ${w.slice(11, 16)}` : '')
const NODE_KOSONG = { olt: [], odc: [], odp: [] }

export default function PetaPelanggan() {
  const admin = isAdmin()
  const [items, setItems] = useState([])
  const [jaringan, setJaringan] = useState(NODE_KOSONG)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [sembunyi, setSembunyi] = useState(bacaSembunyiTersimpan)
  const [layers, setLayers] = useState(bacaLayerTersimpan)
  const [mode, setMode] = useState('pelanggan')    // tab panel samping: pelanggan | jaringan | riwayat
  const [search, setSearch] = useState('')
  const [sel, setSel] = useState(null)             // { tipe: 'pelanggan'|'olt'|'odc'|'odp', key: username|id }
  const [editing, setEditing] = useState(null)     // { tipe, key (null = baru), lat, lng, form? } -- mode atur lokasi/edit titik
  const [saving, setSaving] = useState(false)
  const [tautan, setTautan] = useState(null)       // ppp_username yg modal tautan ODP-nya sedang terbuka
  const [riwayat, setRiwayat] = useState(null)
  const [modeWarna, setModeWarna] = useState(bacaModeTersimpan)
  const [online, setOnline] = useState(null)       // Set username PPPoE aktif (mode Koneksi), null = belum termuat
  const [onlineError, setOnlineError] = useState('')
  const [onlineAt, setOnlineAt] = useState(null)
  const [zoom, setZoom] = useState(5)
  // Overlay Bangunan (checkbox kontrol layer Leaflet) -- diikuti ke state React murni utk menampilkan
  // hint "perbesar peta" saat aktif tapi zoom belum cukup dekat (lihat AMBANG_ZOOM_BANGUNAN).
  const [bangunanAktif, setBangunanAktif] = useState(false)
  // Legenda: di HP default dilipat (isinya ~40% tinggi peta); di desktop selalu terbuka (CSS mengabaikan lipatan).
  const [legendBuka, setLegendBuka] = useState(() => !window.matchMedia('(max-width: 860px)').matches)
  const [params, setParams] = useSearchParams()

  const mapEl = useRef(null)
  const mapColEl = useRef(null)
  const mapRef = useRef(null)
  const garisLayerRef = useRef(null)
  const pelangganLayerRef = useRef(null)
  const nodeLayerRef = useRef(null)
  const editMarkerRef = useRef(null)
  const sudahFitRef = useRef(false)
  const editingRef = useRef(null)
  editingRef.current = editing

  const load = useCallback(async () => {
    try {
      const [p, j] = await Promise.all([api.get('/peta-pelanggan'), api.get('/peta-jaringan')])
      setItems(p.data.items)
      setJaringan(j.data)
      setError('')
    } catch (e) {
      setError(e.response?.data?.message || 'Gagal memuat data peta')
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { load() }, [load])

  // --- Inisialisasi peta (sekali) ---
  useEffect(() => {
    if (!mapEl.current || mapRef.current) return
    const map = L.map(mapEl.current, { zoomControl: true }).setView([-2.5, 118], 5)
    const osm = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    })
    // Citra satelit -- berguna utk mencari atap rumah pelanggan persis saat mengatur lokasi.
    const satelitCitra = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
      maxZoom: 19,
      attribution: 'Tiles &copy; Esri'
    })
    // Overlay nama jalan (transparan) di atas citra -- meniru mode "Satelit" hybrid Google Maps,
    // krn World_Imagery sendiri cuma citra polos tanpa jalan/label.
    const satelitJalan = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}', {
      maxZoom: 19
    })
    const satelit = L.layerGroup([satelitCitra, satelitJalan])
    // Outline bangunan OSM (fase 4) -- overlay OPSIONAL (checkbox, bukan pilihan tunggal spt Peta/
    // Satelit), meniru kotak2 bangunan yg terlihat di mode satelit Google Maps supaya lebih gampang
    // mencocokkan atap rumah pelanggan. Sengaja tidak otomatis nyala & baru memuat data saat zoom
    // dekat (AMBANG_ZOOM_BANGUNAN) -- data dari Overpass API, jangan dipanggil tiap pan/zoom di zoom jauh.
    const bangunanLayer = L.layerGroup()
    map.createPane('bangunan').style.zIndex = 350 // di atas tile (200), di bawah garis (390) & pin (400)
    osm.addTo(map)
    L.control.layers({ Peta: osm, Satelit: satelit }, { Bangunan: bangunanLayer }, { position: 'topright' }).addTo(map)
    // Garis penghubung di pane sendiri DI BAWAH pin pelanggan (overlayPane = 400), supaya tidak
    // menutupi titik & tidak perlu bringToBack() tiap digambar ulang.
    map.createPane('garis').style.zIndex = 390
    garisLayerRef.current = L.layerGroup().addTo(map)
    pelangganLayerRef.current = L.layerGroup().addTo(map)
    nodeLayerRef.current = L.layerGroup().addTo(map)

    // --- Overlay Bangunan: muat ulang dari /api/peta-bangunan saat aktif & zoom cukup dekat ---
    let bangunanAktif = false
    let bangunanTimer = null
    const jadwalMuatBangunan = () => { clearTimeout(bangunanTimer); bangunanTimer = setTimeout(muatBangunan, 400) }
    async function muatBangunan() {
      if (!bangunanAktif || map.getZoom() < AMBANG_ZOOM_BANGUNAN) { bangunanLayer.clearLayers(); return }
      const b = map.getBounds()
      try {
        const { data } = await api.get('/peta-bangunan', {
          params: { selatan: b.getSouth(), barat: b.getWest(), utara: b.getNorth(), timur: b.getEast() }
        })
        bangunanLayer.clearLayers()
        L.geoJSON(data, {
          pane: 'bangunan',
          style: { color: '#fff', weight: 1.4, opacity: 0.85, fillColor: '#fff', fillOpacity: 0.1 },
          interactive: false // outline murni visual, jangan menelan klik yg dimaksud utk pin/peta di bawahnya
        }).addTo(bangunanLayer)
      } catch { /* overlay opsional -- kegagalan (mis. Overpass lambat/turun) jangan sampai merusak peta utama */ }
    }
    map.on('overlayadd', e => { if (e.name === 'Bangunan') { bangunanAktif = true; setBangunanAktif(true); jadwalMuatBangunan() } })
    map.on('overlayremove', e => { if (e.name === 'Bangunan') { bangunanAktif = false; setBangunanAktif(false); bangunanLayer.clearLayers() } })
    map.on('moveend', jadwalMuatBangunan)

    // Klik di peta saat mode atur lokasi = pindahkan pin ke titik itu (selain menggeser pin).
    map.on('click', e => {
      const ed = editingRef.current
      if (!ed) return
      setEditing({ ...ed, lat: e.latlng.lat, lng: e.latlng.lng })
    })
    map.on('zoomend', () => setZoom(map.getZoom()))
    setZoom(map.getZoom())
    mapRef.current = map

    // Kontainer bisa berubah ukuran (sidebar dilipat, rotasi HP) -- tanpa ini tile terpotong.
    const ro = new ResizeObserver(() => map.invalidateSize())
    ro.observe(mapEl.current)
    return () => { clearTimeout(bangunanTimer); ro.disconnect(); map.remove(); mapRef.current = null }
  }, [])

  const tampil = useMemo(
    () => items.filter(i => !sembunyi[modeWarna].includes(kategoriPin(i, modeWarna, online))),
    [items, sembunyi, modeWarna, online]
  )
  const selUser = sel && sel.tipe === 'pelanggan' ? sel.key : null
  const editTipe = editing ? editing.tipe : null
  const editKey = editing ? editing.key : null

  // --- Gambar pin pelanggan tiap data/filter/mode berubah ---
  // Di zoom < CLUSTER_MAX_ZOOM pin berdekatan digabung jadi bubble bernomor (klik = zoom ke anggotanya).
  // Pin yg sedang dipilih/diedit TIDAK PERNAH ikut digabung supaya tidak hilang ditelan bubble.
  useEffect(() => {
    const layer = pelangganLayerRef.current
    const map = mapRef.current
    if (!layer || !map) return
    layer.clearLayers()
    if (!layers.pelanggan) return

    const gambarPin = i => {
      const warna = warnaPin(i, modeWarna, online)
      const m = L.circleMarker([i.lat, i.lng], {
        radius: i.ppp_username === selUser ? 11 : 8,
        color: i.mencurigakan ? '#111' : '#fff',
        weight: i.ppp_username === selUser ? 3 : 2,
        fillColor: warna,
        fillOpacity: 0.95,
        bubblingMouseEvents: false // klik pin jangan ikut terhitung klik peta (mode atur lokasi)
      })
      m.bindTooltip(i.nama || i.ppp_username, { direction: 'top', offset: [0, -6] })
      m.on('click', () => setSel({ tipe: 'pelanggan', key: i.ppp_username }))
      m.addTo(layer)
    }

    const kluster = zoom < CLUSTER_MAX_ZOOM
    const titik = []
    for (const i of tampil) {
      if (i.lat === null) continue
      if (editTipe === 'pelanggan' && editKey === i.ppp_username) continue // digantikan pin draggable
      if (!kluster || i.ppp_username === selUser) { gambarPin(i); continue }
      const px = map.project([i.lat, i.lng], zoom)
      titik.push({ x: px.x, y: px.y, lat: i.lat, lng: i.lng, item: i })
    }
    for (const g of gridCluster(titik, CELL_PX)) {
      if (g.members.length === 1) { gambarPin(g.members[0].item); continue }
      const warna = warnaKluster(g.members.map(m => warnaPin(m.item, modeWarna, online)))
      const m = L.marker([g.lat, g.lng], { icon: clusterIcon(g.members.length, warna), zIndexOffset: 100 })
      m.bindTooltip(`${g.members.length} pelanggan`, { direction: 'top', offset: [0, -14] })
      m.on('click', () => map.fitBounds(L.latLngBounds(g.members.map(x => [x.lat, x.lng])), { padding: [60, 60], maxZoom: 18 }))
      m.addTo(layer)
    }
  }, [tampil, selUser, editTipe, editKey, layers.pelanggan, zoom, modeWarna, online])

  // --- Gambar titik OLT / ODC / ODP ---
  useEffect(() => {
    const layer = nodeLayerRef.current
    if (!layer) return
    layer.clearLayers()
    for (const tipe of ['olt', 'odc', 'odp']) {
      if (!layers[tipe]) continue
      for (const n of jaringan[tipe]) {
        if (n.lat === null) continue
        if (editTipe === tipe && editKey === n.id) continue // digantikan pin draggable
        const dipilih = !!sel && sel.tipe === tipe && sel.key === n.id
        const icon = tipe === 'odp'
          ? nodeIcon('odp', { teks: `${n.terpakai}/${n.kapasitas}`, penuh: n.terpakai >= n.kapasitas, dipilih })
          : nodeIcon(tipe, { dipilih })
        const m = L.marker([n.lat, n.lng], { icon, zIndexOffset: dipilih ? 500 : 0 })
        m.bindTooltip(n.nama, { direction: 'top', offset: [0, -14] })
        m.on('click', () => setSel({ tipe, key: n.id }))
        m.addTo(layer)
      }
    }
  }, [jaringan, layers, sel, editTipe, editKey])

  // --- Garis penghubung: OLT->ODC, ODC->ODP, ODP->pelanggan (hanya bila kedua ujungnya sedang tampil) ---
  useEffect(() => {
    const layer = garisLayerRef.current
    if (!layer) return
    layer.clearLayers()
    if (!layers.garis) return
    const gaya = (color, weight, extra) => ({ pane: 'garis', interactive: false, color, weight, opacity: 0.8, ...extra })
    const petakan = arr => new Map(arr.filter(n => n.lat !== null).map(n => [n.id, n]))
    const olt = petakan(jaringan.olt)
    const odc = petakan(jaringan.odc)
    const odp = petakan(jaringan.odp)
    if (layers.olt && layers.odc) {
      for (const c of jaringan.odc) {
        const o = olt.get(c.olt_id)
        if (o) L.polyline([[o.lat, o.lng], [c.lat, c.lng]], gaya(WARNA_NODE.olt, 3)).addTo(layer)
      }
    }
    if (layers.odc && layers.odp) {
      for (const p of jaringan.odp) {
        const c = odc.get(p.odc_id)
        if (c) L.polyline([[c.lat, c.lng], [p.lat, p.lng]], gaya(WARNA_NODE.odc, 2.5)).addTo(layer)
      }
    }
    // Garis ke pelanggan disembunyikan selama pin pelanggan digabung jadi bubble (zoom jauh) -- ratusan
    // garis putus-putus ke pusat bubble cuma bikin peta berantakan.
    if (layers.odp && layers.pelanggan && zoom >= CLUSTER_MAX_ZOOM) {
      for (const i of tampil) {
        const p = i.lat !== null && i.odp_id ? odp.get(i.odp_id) : null
        if (p) L.polyline([[p.lat, p.lng], [i.lat, i.lng]], gaya(WARNA_NODE.odp, 1.5, { dashArray: '4 4', opacity: 0.7 })).addTo(layer)
      }
    }
  }, [jaringan, layers, tampil, zoom])

  // Fit awal ke kluster utama (titik mencurigakan diabaikan supaya peta tidak ter-zoom-out sampai
  // ke Sumatra). Sekali saja -- reload setelah simpan tidak boleh melempar posisi pandang user.
  useEffect(() => {
    const map = mapRef.current
    if (!map || sudahFitRef.current || loading) return
    const titik = [
      ...items.filter(i => i.lat !== null && !i.mencurigakan).map(i => [i.lat, i.lng]),
      ...['olt', 'odc', 'odp'].flatMap(t => jaringan[t].filter(n => n.lat !== null).map(n => [n.lat, n.lng]))
    ]
    sudahFitRef.current = true
    if (titik.length) map.fitBounds(titik, { padding: [40, 40], maxZoom: 17 })
  }, [items, jaringan, loading])

  // --- Pin draggable utk mode atur lokasi / edit titik ---
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (!editing) {
      if (editMarkerRef.current) { editMarkerRef.current.remove(); editMarkerRef.current = null }
      return
    }
    if (!editMarkerRef.current) {
      let icon
      if (editing.tipe === 'pelanggan') {
        const it = items.find(x => x.ppp_username === editing.key)
        icon = pinIcon(warnaStatus(it && it.status))
      } else {
        const n = editing.key !== null ? jaringan[editing.tipe].find(x => x.id === editing.key) : null
        icon = editing.tipe === 'odp'
          ? nodeIcon('odp', { teks: n ? `${n.terpakai}/${n.kapasitas}` : '+', dipilih: true })
          : nodeIcon(editing.tipe, { teks: n ? undefined : '+', dipilih: true })
      }
      const mk = L.marker([editing.lat, editing.lng], { draggable: true, icon, zIndexOffset: 1000 }).addTo(map)
      mk.on('dragend', () => {
        const p = mk.getLatLng()
        const ed = editingRef.current
        if (ed) setEditing({ ...ed, lat: p.lat, lng: p.lng })
      })
      editMarkerRef.current = mk
    } else {
      editMarkerRef.current.setLatLng([editing.lat, editing.lng])
    }
  }, [editing, items, jaringan])

  function bersihkanEditMarker() {
    if (editMarkerRef.current) { editMarkerRef.current.remove(); editMarkerRef.current = null }
  }

  // HP: peta ada DI ATAS daftar, jadi ketukan di daftar/tombol +ODP tak terlihat hasilnya kalau peta di luar layar.
  // Breakpoint 860px sama dgn CSS .peta-wrap.
  function gulirKePeta() {
    if (!mapColEl.current || !window.matchMedia('(max-width: 860px)').matches) return
    mapColEl.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  function terbangKe(lat, lng, zoomMin) {
    const map = mapRef.current
    if (map) map.flyTo([lat, lng], Math.max(map.getZoom(), zoomMin), { duration: 0.6 })
  }

  function pusatPeta() {
    const c = mapRef.current.getCenter()
    return { lat: c.lat, lng: c.lng }
  }

  function mulaiAtur(it) {
    if (!mapRef.current) return
    gulirKePeta()
    setNotice('')
    setSel({ tipe: 'pelanggan', key: it.ppp_username })
    let lat, lng
    if (it.lat !== null && !it.mencurigakan) {
      ;({ lat, lng } = it)
      terbangKe(lat, lng, 18)
    } else {
      // Belum ada lokasi / koordinat mencurigakan: mulai dari tengah tampilan peta SAAT INI (user
      // biasanya sudah menggeser peta ke daerah pelanggan itu), bukan dari koordinat yg salah.
      ;({ lat, lng } = pusatPeta())
    }
    // Hapus marker lama supaya efek di atas bikin ulang dgn warna status pelanggan yg baru.
    bersihkanEditMarker()
    setEditing({ tipe: 'pelanggan', key: it.ppp_username, lat, lng })
  }

  function mulaiNodeBaru(tipe) {
    if (!mapRef.current) return
    gulirKePeta()
    setNotice(''); setError(''); setSel(null)
    bersihkanEditMarker()
    const { lat, lng } = pusatPeta()
    setEditing({
      tipe, key: null, lat, lng,
      form: tipe === 'odc'
        ? { nama: '', olt_id: '', olt_pon: '', keterangan: '' }
        : { nama: '', odc_id: '', kapasitas: 8, keterangan: '' }
    })
  }

  function mulaiNodeUbah(tipe, n) {
    if (!mapRef.current) return
    gulirKePeta()
    setNotice(''); setError('')
    setSel({ tipe, key: n.id })
    let lat, lng
    if (n.lat !== null) { ({ lat, lng } = n); terbangKe(lat, lng, 18) } else ({ lat, lng } = pusatPeta())
    bersihkanEditMarker()
    const form = tipe === 'odc'
      ? { nama: n.nama, olt_id: n.olt_id ?? '', olt_pon: n.olt_pon ?? '', keterangan: n.keterangan ?? '' }
      : tipe === 'odp'
        ? { nama: n.nama, odc_id: n.odc_id ?? '', kapasitas: n.kapasitas, keterangan: n.keterangan ?? '' }
        : undefined
    setEditing({ tipe, key: n.id, lat, lng, form })
  }

  async function simpanEdit() {
    if (!editing) return
    const { tipe, key, lat, lng, form } = editing
    setSaving(true)
    try {
      let pesan
      if (tipe === 'pelanggan') {
        await api.put(`/peta-pelanggan/${encodeURIComponent(key)}/lokasi`, { latitude: lat, longitude: lng })
        const nama = (items.find(x => x.ppp_username === key) || {}).nama || key
        pesan = `Lokasi ${nama} tersimpan.`
      } else if (tipe === 'olt') {
        await api.put(`/peta-jaringan/olt/${key}/lokasi`, { latitude: lat, longitude: lng })
        pesan = `Lokasi ${(jaringan.olt.find(o => o.id === key) || {}).nama || 'OLT'} tersimpan.`
      } else {
        const body = { ...form, latitude: lat, longitude: lng }
        if (key === null) await api.post(`/peta-jaringan/${tipe}`, body)
        else await api.put(`/peta-jaringan/${tipe}/${key}`, body)
        pesan = `${LABEL_TIPE[tipe]} "${String(form.nama).trim()}" tersimpan.`
      }
      setEditing(null)
      await load()
      setError('')
      setNotice(pesan)
    } catch (e) {
      setNotice('')
      setError(e.response?.data?.message || 'Gagal menyimpan')
    } finally { setSaving(false) }
  }

  function batalEdit() { setEditing(null); setError('') }

  async function hapusNode(tipe, n) {
    if (!window.confirm(`Hapus ${LABEL_TIPE[tipe]} "${n.nama}"?`)) return
    try {
      await api.delete(`/peta-jaringan/${tipe}/${n.id}`)
      setSel(null)
      await load()
      setError('')
      setNotice(`${LABEL_TIPE[tipe]} "${n.nama}" dihapus.`)
    } catch (e) {
      setNotice('')
      setError(e.response?.data?.message || 'Gagal menghapus')
    }
  }

  function fokusPelanggan(it) {
    gulirKePeta()
    setSel({ tipe: 'pelanggan', key: it.ppp_username })
    if (it.lat !== null) terbangKe(it.lat, it.lng, 17)
  }

  function fokusNode(tipe, n) {
    gulirKePeta()
    setSel({ tipe, key: n.id })
    if (n.lat !== null) terbangKe(n.lat, n.lng, 17)
  }

  // Klik kategori di legenda = sembunyikan/tampilkan (pin di peta DAN baris di daftar).
  function ubahSembunyi(kunci) {
    setSembunyi(prev => {
      const lama = prev[modeWarna]
      const next = { ...prev, [modeWarna]: lama.includes(kunci) ? lama.filter(x => x !== kunci) : [...lama, kunci] }
      try { localStorage.setItem(LS_SEMBUNYI, JSON.stringify(next)) } catch {}
      return next
    })
  }

  function ubahLayer(k) {
    setLayers(prev => {
      const next = { ...prev, [k]: !prev[k] }
      try { localStorage.setItem(LS_LAYER, JSON.stringify(next)) } catch {}
      return next
    })
  }

  // Riwayat cuma dimuat saat tabnya dibuka (admin saja -- endpoint-nya 403 utk teknisi).
  const muatRiwayat = useCallback(async () => {
    try {
      const res = await api.get('/peta-jaringan/log', { params: { limit: 150 } })
      setRiwayat(res.data.items)
    } catch (e) {
      setRiwayat([])
      setError(e.response?.data?.message || 'Gagal memuat riwayat')
    }
  }, [])
  useEffect(() => { if (mode === 'riwayat') muatRiwayat() }, [mode, muatRiwayat])

  // Status online (mode "Koneksi", default) -- satu panggilan Mikrotik utk seluruh peta (endpoint yg
  // sama dgn halaman Daftar Pelanggan), diulang tiap 60 dtk HANYA selama mode ini aktif. Gagal (Mikrotik tak
  // terjangkau) -> pesan di legenda; data lama dipertahankan, pin tidak tiba2 jadi "offline semua".
  const muatOnline = useCallback(async () => {
    try {
      const res = await api.get('/pelanggan-kontak/mikrotik/online')
      setOnline(new Set(res.data.online))
      setOnlineAt(new Date())
      setOnlineError('')
    } catch (e) {
      setOnlineError(e.response?.data?.message || 'Gagal mengambil status online dari Mikrotik')
    }
  }, [])
  useEffect(() => {
    if (modeWarna !== 'koneksi') return
    muatOnline()
    const t = setInterval(muatOnline, 60000)
    return () => clearInterval(t)
  }, [modeWarna, muatOnline])

  function ubahModeWarna(m) {
    setModeWarna(m)
    try { localStorage.setItem(LS_MODE, m) } catch {}
  }

  // Link halaman Ambil Lokasi GPS utk dibagikan ke teknisi (WA dsb). Halaman itu tetap wajib login.
  async function salinLinkGps(username) {
    const url = `${window.location.origin}/ambil-lokasi/${encodeURIComponent(username)}`
    try {
      await navigator.clipboard.writeText(url)
      setError(''); setNotice('Link GPS disalin. Kirim ke teknisi — dibuka di HP saat berada di lokasi pelanggan.')
    } catch {
      window.prompt('Salin link GPS ini:', url) // clipboard diblokir (mis. bukan HTTPS): tampilkan utk disalin manual
    }
  }

  // ?fokus=username (dari tombol "Lihat di Peta" di halaman Ambil Lokasi GPS) -- pilih & terbang ke
  // pelanggan itu begitu data termuat, lalu parameter dibuang supaya reload tidak mengulang.
  useEffect(() => {
    const u = params.get('fokus')
    if (!u || loading || !mapRef.current) return
    const it = items.find(x => x.ppp_username === u)
    if (it) { sudahFitRef.current = true; setSel({ tipe: 'pelanggan', key: u }); if (it.lat !== null) terbangKe(it.lat, it.lng, 18) }
    setParams({}, { replace: true })
  }, [params, loading, items])

  async function setelahTautan(pesan) {
    setTautan(null)
    await load()
    setError('')
    setNotice(pesan)
  }

  const q = search.trim().toLowerCase()
  const cocok = i => !q || (i.nama || '').toLowerCase().includes(q) || i.ppp_username.toLowerCase().includes(q) || (i.alamat || '').toLowerCase().includes(q)
  // Satu daftar, urut: lokasi mencurigakan (perlu dikoreksi) -> belum ada lokasi (perlu diisi) -> sisanya
  // (urutan nama dari server dipertahankan; Array.sort stabil).
  const prioritas = i => (i.mencurigakan ? 0 : i.lat === null ? 1 : 2)
  const daftar = tampil.filter(cocok).sort((a, b) => prioritas(a) - prioritas(b))
  const jumlahCek = items.filter(i => i.mencurigakan).length
  const detail = selUser ? items.find(i => i.ppp_username === selUser) : null
  const detailOdp = detail && detail.odp_id ? jaringan.odp.find(p => p.id === detail.odp_id) : null
  const selNode = sel && sel.tipe !== 'pelanggan' ? (jaringan[sel.tipe] || []).find(n => n.id === sel.key) : null
  const totalBerlokasi = items.filter(i => i.lat !== null && !i.mencurigakan).length
  const nodeCocok = n => !q || n.nama.toLowerCase().includes(q)
  const tautanPelanggan = tautan ? items.find(i => i.ppp_username === tautan) : null

  const infoLayer = [
    { k: 'pelanggan', label: 'Pelanggan', jumlah: items.filter(i => i.lat !== null).length, swatch: { borderRadius: '50%', background: WARNA_STATUS.AKTIF } },
    { k: 'odp', label: 'ODP', jumlah: jaringan.odp.length, swatch: { borderRadius: 6, background: WARNA_NODE.odp } },
    { k: 'odc', label: 'ODC', jumlah: jaringan.odc.length, swatch: { borderRadius: 3, background: WARNA_NODE.odc } },
    { k: 'olt', label: 'OLT', jumlah: jaringan.olt.filter(o => o.lat !== null).length, swatch: { borderRadius: 3, background: WARNA_NODE.olt } },
    { k: 'garis', label: 'Garis', swatch: { height: 3, borderRadius: 2, background: '#6b6b78', alignSelf: 'center' } }
  ]
  const tombolLegend = on => ({
    display: 'flex', alignItems: 'center', gap: 6, border: 'none', cursor: 'pointer', background: 'transparent',
    padding: '3px 4px', fontSize: 12, fontWeight: 600, opacity: on ? 1 : 0.4,
    color: 'var(--text)', textDecoration: on ? 'none' : 'line-through'
  })

  return (
    <div>
      <PageHeader
        title="Peta Pelanggan"
        subtitle={`${totalBerlokasi} dari ${items.length} pelanggan sudah punya lokasi yang valid · ${jaringan.odc.length} ODC · ${jaringan.odp.length} ODP`}
      />
      {error && <Alert type="error">{error}</Alert>}
      {notice && <Alert type="success">{notice}</Alert>}

      <div className={`peta-wrap${editing || detail || selNode ? ' peta-card-open' : ''}`}>
        <div ref={mapColEl} className="peta-map-col">
          <div ref={mapEl} className="peta-map" />

          {/* Legenda: mode warna pin -> kategori warna (klik = sembunyikan/tampilkan, sekaligus filter daftar) -> toggle
              layer. Semua pilihan diingat di browser. */}
          <div className={`peta-legend${legendBuka ? '' : ' peta-legend-tutup'}`}>
            <div className="peta-legend-row" style={{ alignItems: 'center' }}>
              <span className="peta-legend-label" style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-faint)' }}>Warna pin:</span>
              {MODE_WARNA.map(([k, label]) => (
                <button key={k} onClick={() => ubahModeWarna(k)} style={{
                  border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 999,
                  background: modeWarna === k ? 'var(--brand)' : '#eef0f4', color: modeWarna === k ? '#fff' : 'var(--text)'
                }}>{label}</button>
              ))}
              <button className="peta-legend-toggle" onClick={() => setLegendBuka(v => !v)} aria-expanded={legendBuka}
                title="Tampilkan/sembunyikan filter & layer" style={{ alignItems: 'center', gap: 3, border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', padding: '2px 4px' }}>
                Filter {legendBuka ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            </div>
            <div className="peta-legend-row peta-legend-bagian" style={{ borderTop: '1px solid var(--border)', paddingTop: 3, alignItems: 'center' }}>
              {KATEGORI[modeWarna].map(k => {
                const jumlah = items.filter(i => kategoriPin(i, modeWarna, online) === k.kunci).length
                if (k.kunci === 'AKTIF_TAK_TAHU' && jumlah === 0) return null
                const on = !sembunyi[modeWarna].includes(k.kunci)
                return (
                  <button key={k.kunci} onClick={() => ubahSembunyi(k.kunci)} title={on ? 'Klik untuk sembunyikan' : 'Klik untuk tampilkan'} style={tombolLegend(on)}>
                    <span style={gayaTitik(k, 11)} />
                    {k.label} ({jumlah})
                  </button>
                )
              })}
              {jumlahCek > 0 && (
                <span title="Pin bergaris hitam: lokasinya sangat jauh dari pelanggan lain, kemungkinan salah input" style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, padding: '3px 4px', color: 'var(--danger)' }}>
                  <AlertTriangle size={12} /> Cek lokasi ({jumlahCek})
                </span>
              )}
              {modeWarna === 'koneksi' && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11.5, color: onlineError ? 'var(--danger)' : 'var(--text-muted)' }}>
                  {onlineError || (onlineAt ? `Online diperbarui ${onlineAt.toTimeString().slice(0, 5)}` : 'Memuat status online…')}
                  <button onClick={muatOnline} title="Muat ulang status online" aria-label="Muat ulang status online" style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 2, display: 'flex', color: 'var(--text-muted)' }}><RefreshCw size={12} /></button>
                </span>
              )}
            </div>
            <div className="peta-legend-row peta-legend-bagian" style={{ borderTop: '1px solid var(--border)', paddingTop: 3 }}>
              {infoLayer.map(l => (
                <button key={l.k} onClick={() => ubahLayer(l.k)} title={layers[l.k] ? 'Klik untuk sembunyikan layer' : 'Klik untuk tampilkan layer'} style={tombolLegend(layers[l.k])}>
                  <span style={{ width: 11, height: 11, boxShadow: '0 0 0 1px rgba(0,0,0,.25)', ...l.swatch }} />
                  {l.label}{l.jumlah !== undefined ? ` (${l.jumlah})` : ''}
                </button>
              ))}
            </div>
          </div>

          {!editing && bangunanAktif && zoom < AMBANG_ZOOM_BANGUNAN && (
            <div className="peta-overlay-card" style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
              Perbesar peta lagi untuk melihat outline bangunan.
            </div>
          )}

          {/* Bar mode atur lokasi / form titik jaringan */}
          {editing && editing.tipe === 'pelanggan' && (
            <div className="peta-overlay-card" style={{ borderColor: 'var(--brand)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 13.5 }}>
                <Crosshair size={16} color="var(--brand)" />
                Atur lokasi: {(items.find(x => x.ppp_username === editing.key) || {}).nama || editing.key}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', margin: '4px 0 10px' }}>
                Geser pin, atau klik titik di peta. {editing.lat.toFixed(6)}, {editing.lng.toFixed(6)}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button size="sm" icon={Check} loading={saving} onClick={simpanEdit}>Simpan</Button>
                <Button size="sm" variant="soft" icon={X} disabled={saving} onClick={batalEdit}>Batal</Button>
              </div>
            </div>
          )}
          {editing && editing.tipe !== 'pelanggan' && (
            <KartuEditNode editing={editing} setEditing={setEditing} jaringan={jaringan} saving={saving} onSimpan={simpanEdit} onBatal={batalEdit} />
          )}

          {/* Kartu detail titik jaringan yg diklik */}
          {!editing && selNode && (
            <KartuNode
              tipe={sel.tipe} node={selNode} jaringan={jaringan} items={items} admin={admin}
              onTutup={() => setSel(null)}
              onUbah={() => mulaiNodeUbah(sel.tipe, selNode)}
              onHapus={() => hapusNode(sel.tipe, selNode)}
              onPilihPelanggan={fokusPelanggan}
            />
          )}

          {/* Kartu detail pelanggan yg diklik */}
          {!editing && detail && (
            <div className="peta-overlay-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14.5 }}>{detail.nama || detail.ppp_username}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{detail.ppp_username}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Badge tone={TONE_STATUS[detail.status] || 'neutral'}>{LABEL_STATUS[detail.status] || detail.status}</Badge>
                  <button onClick={() => setSel(null)} aria-label="Tutup" style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-faint)', padding: 2 }}><X size={16} /></button>
                </div>
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '8px 0 10px', lineHeight: 1.5 }}>
                {detail.nama_paket && <div>Paket: {detail.nama_paket}</div>}
                <div>
                  Tagihan:{' '}
                  {detail.tagihan
                    ? <strong style={{ color: detail.tagihan === 'TERLAMBAT' ? 'var(--danger)' : detail.tagihan === 'BELUM' ? 'var(--warning)' : 'var(--success)' }}>{LABEL_TAGIHAN[detail.tagihan]}</strong>
                    : 'belum ada tagihan'}
                  {online !== null && detail.status === 'AKTIF' && (
                    <> · Koneksi: <strong style={{ color: online.has(detail.ppp_username) ? 'var(--success)' : 'var(--danger)' }}>{online.has(detail.ppp_username) ? 'Online' : 'Offline'}</strong></>
                  )}
                </div>
                <div>{detail.alamat || 'Alamat belum diisi'}</div>
                <div>
                  ODP:{' '}
                  {detailOdp ? (
                    <button onClick={() => fokusNode('odp', detailOdp)} style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', color: 'var(--brand)', fontWeight: 600, fontSize: 12.5, fontFamily: 'inherit' }}>
                      {detailOdp.nama}{detail.odp_port ? ` · port ${detail.odp_port}` : ''}
                    </button>
                  ) : 'belum ditautkan'}
                </div>
                {detail.mencurigakan && <div style={{ color: 'var(--danger)', fontWeight: 600 }}>Lokasi mencurigakan: sangat jauh dari pelanggan lain. Mohon dikoreksi.</div>}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {detail.lat !== null && (
                  <a href={`https://www.google.com/maps/dir/?api=1&destination=${detail.lat},${detail.lng}`} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                    <Button size="sm" icon={Navigation} type="button">Navigasi</Button>
                  </a>
                )}
                {detail.no_hp && (
                  <a href={`https://wa.me/${detail.no_hp}`} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                    <Button size="sm" variant="soft" icon={MessageCircle} type="button">WhatsApp</Button>
                  </a>
                )}
                <Button size="sm" variant="soft" icon={MapPin} onClick={() => mulaiAtur(detail)}>
                  {detail.lat === null ? 'Atur Lokasi' : 'Ubah Lokasi'}
                </Button>
                <Button size="sm" variant="soft" icon={Link2} onClick={() => setTautan(detail.ppp_username)}>
                  {detail.odp_id ? 'Ubah ODP' : 'Tautkan ODP'}
                </Button>
                <Button size="sm" variant="soft" icon={Smartphone} onClick={() => salinLinkGps(detail.ppp_username)} title="Salin link utk teknisi mengambil lokasi GPS dari HP">
                  Link GPS
                </Button>
              </div>
            </div>
          )}
        </div>

        <Card style={{ padding: 14, display: 'flex', flexDirection: 'column', minHeight: 0 }} className="peta-side">
          <Tabs
            active={mode}
            onChange={setMode}
            tabs={[['pelanggan', 'Pelanggan'], ['jaringan', 'Jaringan'], ...(admin ? [['riwayat', 'Riwayat']] : [])]}
          />

          {mode === 'pelanggan' && (
            <>
              <div style={{ position: 'relative', marginBottom: 10 }}>
                <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: 'var(--text-faint)' }} />
                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cari nama, username, alamat" style={{ paddingLeft: 30 }} />
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginBottom: 8 }}>
                Klik pelanggan untuk detail & atur lokasi. Lingkaran kosong = belum ada lokasi, ⚠ = lokasi perlu dicek. Klik warna di legenda peta untuk menyaring.
              </div>
              <div className="peta-list">
                {loading ? <div style={{ padding: 16, color: 'var(--text-muted)', fontSize: 13 }}>Memuat...</div>
                  : daftar.length === 0 ? <EmptyState title="Tidak ada pelanggan" hint="Coba ubah pencarian atau filter warna di legenda." />
                  : daftar.map(i => {
                    const kat = KATEGORI[modeWarna].find(x => x.kunci === kategoriPin(i, modeWarna, online)) || KAT_BELUM_LOKASI
                    return (
                      <button key={i.ppp_username} onClick={() => fokusPelanggan(i)}
                        className="peta-row" style={{ background: i.ppp_username === selUser ? 'var(--brand-soft)' : 'transparent' }} title={kat.label}>
                        <span style={{ ...gayaTitik(kat, 10), marginTop: 4 }} />
                        <span style={{ minWidth: 0, textAlign: 'left', flex: 1 }}>
                          <span style={{ display: 'block', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{i.nama || i.ppp_username}</span>
                          <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{i.alamat || i.ppp_username}</span>
                        </span>
                        {i.mencurigakan && <AlertTriangle size={14} color="var(--danger)" style={{ flexShrink: 0, marginTop: 3 }} aria-label="Lokasi perlu dicek" />}
                      </button>
                    )
                  })}
              </div>
            </>
          )}

          {mode === 'jaringan' && (
            <>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <Button size="sm" icon={Plus} onClick={() => mulaiNodeBaru('odc')} disabled={!!editing}>ODC</Button>
                <Button size="sm" icon={Plus} onClick={() => mulaiNodeBaru('odp')} disabled={!!editing}>ODP</Button>
              </div>
              <div style={{ position: 'relative', marginBottom: 10 }}>
                <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: 'var(--text-faint)' }} />
                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cari nama titik" style={{ paddingLeft: 30 }} />
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginBottom: 8 }}>
                Tombol + menaruh pin di tengah peta yang sedang tampil — geser ke lokasi sebenarnya, isi nama, lalu simpan.
              </div>
              <div className="peta-list">
                {['olt', 'odc', 'odp'].map(tipe => {
                  const list = jaringan[tipe].filter(nodeCocok)
                  return (
                    <div key={tipe} style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-faint)', letterSpacing: 0.4, padding: '2px 6px' }}>
                        {LABEL_TIPE[tipe]} ({jaringan[tipe].length})
                      </div>
                      {list.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-faint)', padding: '4px 6px' }}>
                        {jaringan[tipe].length === 0 ? (tipe === 'olt' ? 'Belum ada OLT di halaman Perangkat OLT.' : `Belum ada ${LABEL_TIPE[tipe]}.`) : 'Tidak cocok.'}
                      </div>}
                      {list.map(n => {
                        const penuh = tipe === 'odp' && n.terpakai >= n.kapasitas
                        const kecil = tipe === 'odp' ? `${n.terpakai}/${n.kapasitas} port${n.odc_id ? ` · ${(jaringan.odc.find(c => c.id === n.odc_id) || {}).nama || ''}` : ''}`
                          : tipe === 'odc' ? `${n.jumlah_odp} ODP${n.olt_id ? ` · ${(jaringan.olt.find(o => o.id === n.olt_id) || {}).nama || ''}${n.olt_pon ? ` ${n.olt_pon}` : ''}` : ''}`
                          : n.lat === null ? 'Belum ada lokasi — klik untuk atur' : 'Klik untuk lihat'
                        return (
                          <button key={n.id} className="peta-row" onClick={() => (n.lat === null ? mulaiNodeUbah('olt', n) : fokusNode(tipe, n))}
                            style={{ background: sel && sel.tipe === tipe && sel.key === n.id ? 'var(--brand-soft)' : 'transparent' }}>
                            <span style={{ width: 10, height: 10, borderRadius: tipe === 'odp' ? 5 : 2, background: penuh ? WARNA_ODP_PENUH : WARNA_NODE[tipe], flexShrink: 0, marginTop: 4 }} />
                            <span style={{ minWidth: 0, textAlign: 'left' }}>
                              <span style={{ display: 'block', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{n.nama}</span>
                              <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{kecil}</span>
                            </span>
                          </button>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
            </>
          )}

          {mode === 'riwayat' && (
            <>
              <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginBottom: 8 }}>
                150 perubahan peta terakhir (lokasi pelanggan, ODC/ODP, tautan) — siapa mengubah apa.
              </div>
              <div className="peta-list">
                {riwayat === null ? <div style={{ padding: 16, color: 'var(--text-muted)', fontSize: 13 }}>Memuat...</div>
                  : riwayat.length === 0 ? <EmptyState title="Belum ada riwayat" hint="Perubahan peta akan tercatat di sini." />
                  : riwayat.map(r => (
                    <div key={r.id} style={{ padding: '7px 6px', borderBottom: '1px solid var(--border)', fontSize: 12.5, lineHeight: 1.45 }}>
                      <div style={{ color: 'var(--text-faint)', fontSize: 11 }}>{formatWaktu(r.waktu)} · {r.username}</div>
                      <div>{r.ringkasan}</div>
                    </div>
                  ))}
              </div>
            </>
          )}
        </Card>
      </div>

      {tautanPelanggan && (
        <TautanOdpModal pelanggan={tautanPelanggan} jaringan={jaringan} items={items} onClose={() => setTautan(null)} onSimpan={setelahTautan} />
      )}
    </div>
  )
}
